<template>
  <div :class="componentClasses">
    <div class="sidenav-collapsed">
      <button
        class="stk-reset sidenav-expand"
        @click="emit('onExpand')"
      >
        <Icon icon="fas fa-angle-double-right" />
      </button>

      <router-link
        class="sidenav-yourstake-icon"
        :to="{ name: 'home' }"
      >
        <YourStakeIcon />
      </router-link>

      <nav class="sidenav-menu">
        <SideNavIcon
          v-for="menuItem in firstMenuItems"
          :key="menuItem.label"
          :menu-item="menuItem"
        />
      </nav>

      <nav class="sidenav-menu">
        <SideNavIcon
          v-for="menuItem in advisorCoreItems"
          :key="menuItem.label"
          :menu-item="menuItem"
        />
      </nav>

      <nav class="sidenav-menu">
        <SideNavIcon
          v-for="menuItem in valuesCoreItems"
          :key="menuItem.label"
          :menu-item="menuItem"
        />
      </nav>

      <nav
        v-if="advisorStore.hasFeature('FAFN Direct Indexing') || advisorStore.hasFeature('Client Groups')"
        class="sidenav-menu"
      >
        <SideNavIcon
          v-if="advisorStore.hasFeature('FAFN Direct Indexing')"
          :menu-item="{
            href: '/advisor-dashboard/direct-indexing/',
            icon: 'fas fa-network-wired',
            label: 'VADIS',
            target: '_blank',
            iconAfter: 'fas fa-external-link-alt',
          }"
        />
        <SideNavIcon
          v-if="advisorStore.hasFeature('Client Groups')"
          :menu-item="{
            to: { name: 'organizations' },
            icon: 'fas fa-building',
            label: 'Organizations',
          }"
        />
      </nav>

      <footer class="sidenav-bottom">
        <div class="sidenav-bottom-user">
          <a
            class="sidenav-user-logo"
            href="/advisordashboard/update-logo/"
            title="Click to update your logo"
          >
            <img
              v-if="advisorStore.advisor?.firm.logo"
              :src="advisorStore.advisor?.firm.logo"
              alt="Your logo"
            >
            <span
              v-else
              class="sidenav-user-placeholder"
            >
              <Icon icon="fas fa-image" />
            </span>
          </a>
        </div>

        <SideNavDropup right />

        <nav class="sidenav-menu">
          <SideNavIcon
            v-for="menuItem in footerItems"
            :key="menuItem.label"
            :menu-item="menuItem"
          />
        </nav>
      </footer>
    </div>

    <div class="sidenav-full">
      <div class="sidenav-body">
        <header class="sidenav-header">
          <router-link
            class="sidenav-logo"
            :to="{ name: 'home' }"
          >
            <YourStakeLogo />
          </router-link>

          <button
            class="stk-reset sidenav-collapse"
            @click="emit('onCollapse')"
          >
            <Icon icon="fas fa-angle-double-left" />
          </button>
        </header>

        <Callout
          v-if="advisorStore.advisor?.firm_is_pending"
          class="sidenav-pending"
          status="warning"
        >
          We're reviewing your account to make sure you're a registered financial
          advisor, and will activate you shortly.
        </Callout>

        <nav class="sidenav-menu">
          <SideNavLink
            v-for="menuItem in firstMenuItems"
            :key="menuItem.label"
            :menu-item="menuItem"
          />
        </nav>

        <AskMenuLink />

        <div class="sidenav-product">
          <AdvisorCoreLogo />
        </div>

        <nav class="sidenav-menu">
          <SideNavLink
            v-for="menuItem in advisorCoreItems"
            :key="menuItem.label"
            :menu-item="menuItem"
          />
        </nav>

        <div class="sidenav-product">
          <ValuesCoreLogo />
        </div>

        <nav class="sidenav-menu">
          <SideNavLink
            v-for="menuItem in valuesCoreItems"
            :key="menuItem.label"
            :menu-item="menuItem"
          />
        </nav>

        <hr class="sidenav-separator" />

        <nav class="sidenav-menu">
          <SideNavLink
            v-if="advisorStore.hasFeature('FAFN Direct Indexing')"
            :menu-item="{
              href: '/advisor-dashboard/direct-indexing/',
              icon: 'fas fa-network-wired',
              label: 'VADIS',
              target: '_blank',
              iconAfter: 'fas fa-external-link-alt',
            }"
          />
          <SideNavLink
            v-if="advisorStore.hasFeature('Client Groups')"
            :menu-item="{
              to: { name: 'organizations' },
              icon: 'fas fa-building',
              label: 'Organizations',
            }"
          />
        </nav>
      </div>

      <footer class="sidenav-footer">
        <div class="sidenav-user">
          <a
            class="sidenav-user-logo"
            href="/advisordashboard/update-logo/"
            title="Click to update your logo"
          >
            <img
              v-if="advisorStore.advisor?.firm.logo"
              :src="advisorStore.advisor?.firm.logo"
              alt="Your logo"
            >
            <span
              v-else
              class="sidenav-user-placeholder"
            >
              <Icon icon="fas fa-image" />
            </span>
          </a>
          <p class="sidenav-user-info">
            <span>Signed in as</span>
            <strong>{{ advisorStore.advisor?.name }}</strong>
          </p>

          <SideNavDropup />
        </div>

        <nav class="sidenav-menu">
          <SideNavLink
            v-for="menuItem in footerItems"
            :key="menuItem.label"
            :menu-item="menuItem"
          />
        </nav>
      </footer>
    </div>

    <div class="sidenav-shadow" />
  </div>
</template>

<script setup>
import YourStakeIcon from "@frontend/ui/components/YourStakeIcon.vue";
import YourStakeLogo from "@frontend/ui/components/YourStakeLogo.vue";
import { useAdvisorStore } from "@frontend/ui/stores/advisorStore";
import { AdvisorCoreLogo, Callout, Icon, ValuesCoreLogo } from "@stakeui";
import { computed } from "vue";
import SideNavDropup from "./SideNavDropup.vue";
import SideNavLink from "./SideNavLink.vue";
import SideNavIcon from "./SideNavIcon.vue";
import AskMenuLink from "@frontend/ui/features/ask-anything/components/AskMenuLink.vue";

const props = defineProps({
  collapsed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['onExpand', 'onCollapse']);

const advisorStore = useAdvisorStore();

const firstMenuItems = computed(() => {
  const items = [
    {
      to: { name: "home" },
      icon: "fas fa-home",
      label: "Home",
    },
    {
      to: { name: "people" },
      icon: "fas fa-users",
      label: "People",
    },
    {
      to: { name: "portfolios" },
      icon: "fas fa-list-alt",
      label: "Portfolios",
    },
  ];


  if (advisorStore.hasFeature("Teams Admin")) {
    items.push({
      to: { name: "teams-index" },
      icon: "fas fa-users",
      label: "Teams",
    });
  }

  items.push({
    to: { name: "setup" },
    icon: "fas fa-cog",
    label: "Setup",
  });

  return items;
});

const advisorCoreItems = computed(() => {
  const items = [];

  if (advisorStore.hasFeature("Portfolio Checkup")) {
    items.push({
      to: { name: "auto-portfolio" },
      icon: "fas fa-chart-bar",
      label: "Portfolio Intelligence",
    });
  } else {
    items.push({
      href: "https://yourstake.org/advisor-core/portfolio-analysis-and-proposal-generation",
      icon: "fas fa-chart-bar",
      label: "Portfolio Intelligence",
      target: "_blank",
      iconAfter: "fas fa-external-link-alt",
    });
  }

  if (advisorStore.hasFeature("Meetings")) {
    items.push({
      to: { name: "meetings" },
      icon: "fas fa-calendar",
      label: "Meeting Assistant",
    });

    if (advisorStore.hasFeature("Action Center")) {
      items.push({
        to: { name: "action-center-inbox" },
        icon: "fas fa-tasks",
        label: "Action Center",
      });
    }
  } else {
    items.push({
      href: "https://yourstake.org/product-tours/ai-note-taker",
      icon: "fas fa-calendar",
      label: "Meeting Assistant",
      target: '_blank',
      iconAfter: "fas fa-external-link-alt",
    });
  }

  return items;
});

const valuesCoreItems = computed(() => {
  const values_items = [];
  values_items.push({
    to: { name: "reports" },
    icon: "fas fa-chart-pie",
    label: "Impact Reports",
    variant: "valuescore",
  });
  if (advisorStore.hasFeature("Fund Screener")) {
    values_items.push({
      to: { name: "fund-screener" },
      icon: "fas fa-search-dollar",
      label: "Fund Screener",
      variant: "valuescore",
    });
    values_items.push({
      to: { name: "portfolio-designer" },
      icon: "fas fa-layer-group",
      label: "Portfolio Designer",
      variant: "valuescore",
    });
  }
  if (advisorStore.hasFeature("Portfolio Builder")) {
    values_items.push({
      href: "/advisordashboard/portfolio-builder/",
      target: "_blank",
      iconAfter: "fas fa-external-link-alt",
      icon: "fas fa-search",
      label: "Company Screener",
      variant: "valuescore",
    });
  }
  return values_items;
});

const footerItems = [
  {
    href: "https://help.yourstake.org/migration/knowledge",
    target: "_blank",
    icon: "fas fa-life-ring",
    label: "Help Center",
    iconAfter: "fas fa-external-link-alt",
  },
  {
    href: "/accounts/logout/",
    icon: "fas fa-sign-out-alt",
    label: "Log out",
  },
];

const componentClasses = computed(() => [
  'sidenav',
  props.collapsed && 'is-collapsed',
]);
</script>

<style>
.sidenav {
  --bg: #fcfcfd;
  background-color: var(--bg);
  position: relative;
  min-height: 100vh;
  text-align: start;
  display: flex;

  &.is-collapsed {
    .sidenav-full {
      opacity: 0;
      pointer-events: none;
      transform: translateX(-100%);
    }

    .sidenav-collapsed {
      pointer-events: auto;
      opacity: 1;
      transform: none;
    }
  }
}

.sidenav-collapsed {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: var(--stk-sidenav-collapsed-width);
  position: absolute;
  z-index: 10;
  min-height: 100vh;
  transform: translateX(-100%);
  pointer-events: none;
  opacity: 0;
  transition-duration: var(--stk-transition-duration);
  transition-property: opacity, transform;

  > .sidenav-menu:last-of-type {
    padding-bottom: 12rem;
  }
}

.sidenav-expand,
.sidenav-collapse {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--stk-text-weak);
  cursor: pointer;

  &:hover {
    color: var(--stk-primary);
  }
}

.sidenav-expand {
  padding: 0.5rem;
}

.sidenav-yourstake-icon {
  cursor: pointer;
  display: flex;
  padding: 0.5rem 0.75rem;
  align-items: center;
  justify-content: center;
}

.sidenav-bottom {
  margin-top: auto;
  position: fixed;
  left: 0;
  bottom: 0;
  backdrop-filter: blur(4px);
  background-color: #fcfcfdcc;
  border-top: 1px solid var(--stk-text-95);
  z-index: 51;
}

.sidenav-bottom-user {
  display: flex;
  padding: 0.5rem;
}

.sidenav-full {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  transition-duration: var(--stk-transition-duration);
  transition-property: transform;
  width: var(--stk-sidenav-width);
  flex-shrink: 0;
}

.sidenav-body {
  padding: 1.5rem 0.75rem 8rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex-grow: 1;
}

.sidenav-header {
  display: flex;
  padding: 0 0.5rem;
  align-items: center;
  justify-content: space-between;
}

.sidenav-pending {
  font-size: 0.875em;
}

.sidenav-logo {
  display: flex;
  height: 2rem;
}

.sidenav-product {
  display: flex;
  padding: 0.5rem 0.75rem 0;
}

.sidenav-separator {
  background-color: var(--stk-text-90);
  height: 1px;
  margin: 0.75rem;
}

.sidenav-menu {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.sidenav-shadow {
  border-right: 1px solid var(--text-85, #dfe2e7);
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.02) 100%
  );
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 0.5rem;
  z-index: 15;
}

.sidenav-user {
  align-items: center;
  display: flex;
  padding: 0 0.5rem 0.5rem;
  gap: 0.5rem;
  line-height: 1.125;
  position: relative;

  span {
    color: var(--stk-text-weak);
    font-size: 0.75em;
  }

  strong {
    display: block;
    font-weight: 600;
  }
}

.sidenav-user-info {
  flex-grow: 1;
}

.sidenav-user-logo,
.sidenav-user-placeholder {
  flex-shrink: 0;
  height: 2rem;
  display: flex;
  align-items: center;
}

.sidenav-user-logo {
  justify-content: end;
  width: 2rem;

  img {
    object-fit: contain;
    object-fit: contain;
    width: auto;
    max-height: 100%;
  }
}

.sidenav-user-placeholder {
  border: 1px dashed var(--stk-border);
  border-radius: 9999px;
  justify-content: center;
  width: 2rem;
}

.sidenav-footer {
  backdrop-filter: blur(4px);
  background-color: #fcfcfdcc;
  border-top: 1px solid var(--stk-text-95);
  margin-top: auto;
  position: fixed;
  left: 0;
  bottom: 0;
  padding: 1rem 0.75rem;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color;
  text-align: start;
  width: var(--stk-sidenav-width);
  flex-shrink: 0;

  &:hover {
    background-color: #fcfcfd;
  }

  .sidenav-menu {
    display: grid;
    grid-template-columns: 1fr auto;
    flex-direction: row;
    font-size: 0.875em;

    a {
      padding-left: 0.375em;
      padding-right: 0.375em;
    }
  }
}
</style>
